// ── "When is this device next due?" hint for the server pusher ───────────────
// Writes users/{uid}.nextAlarmAt — the epoch ms of the soonest upcoming
// notify-moment on this device — so api/cron/send-alarm-pushes.js can ask
// Firestore for the handful of users due right now instead of reading every
// user document every minute.
//
// WHY THIS EXISTS. That cron runs once a minute and used to read the whole
// users collection each time: ~1,440 full scans a day. At ~37 accounts that is
// ~53,000 reads/day against a 50,000/day no-cost cap, so the project ran out of
// Firestore quota every afternoon, the endpoint started failing, and the
// external scheduler eventually disabled the job outright — taking the entire
// push path down with it (2026-09-22). With this field the cron's cost scales
// with alarms actually due rather than with the number of accounts.
//
// The server writes this field too (after each push, and on its hourly sweep),
// which is what keeps installs that predate this file working. What the server
// CANNOT do is notice promptly: it would only find a newly-set alarm on its
// next sweep, up to an hour later. This device knows the moment the data
// changes, which is the whole reason the client half exists.
//
// Covers alarms AND plain notifications, because the cron pushes both.
import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { doc, setDoc, deleteField } from 'firebase/firestore';
import { db } from '../firebase/config';
import { collectUpcomingAlarms, collectUpcomingNotifications } from '../utils/taskLogic';
import { collectUpcomingHabitAlarms, collectUpcomingHabitNotifications } from '../utils/habitLogic';

// Native only: the push path this feeds is android-native by definition (see
// the token filter in send-alarm-pushes.js), so a browser session writing this
// field would just be noise — and could fight with the phone's value.
const isNative = Capacitor.isNativePlatform();

// Survives reloads so a restart doesn't re-write an unchanged value. Purely an
// optimisation: a wrong or missing cache costs one redundant write, never a
// missed alarm.
const CACHE_KEY = 'clarity_nextAlarmAtSent';

function soonestTrigger(args) {
  const all = [
    ...collectUpcomingAlarms(args),
    ...collectUpcomingNotifications(args),
    ...collectUpcomingHabitAlarms(args),
    ...collectUpcomingHabitNotifications(args),
  ];
  let best = null;
  for (const a of all) {
    const t = Number(a.triggerAt);
    if (!Number.isFinite(t)) continue;
    if (best === null || t < best) best = t;
  }
  return best;
}

async function writeHint(uid, next) {
  let cached = null;
  try { cached = window.localStorage.getItem(CACHE_KEY); } catch { /* private mode */ }
  const asStr = next === null ? 'none' : String(next);
  if (cached === `${uid}:${asStr}`) return; // unchanged since the last successful write

  try {
    await setDoc(
      doc(db, 'users', uid),
      { nextAlarmAt: next === null ? deleteField() : next },
      { merge: true },
    );
    try { window.localStorage.setItem(CACHE_KEY, `${uid}:${asStr}`); } catch { /* private mode */ }
  } catch (e) {
    // Leave the cache alone so the next run retries. Never surfaced to the
    // user: the local alarm path is unaffected, and the server's own sweep
    // re-stamps this field anyway.
    // eslint-disable-next-line no-console
    console.warn('[Clarity] nextAlarmAt hint write failed, will retry', e);
  }
}

export function useNextAlarmHint(uid, tasks, calEvents, calReminders, taskOccurrenceOverrides, calEventOccurrenceOverrides, habits = [], habitLogs = {}, habitSkips = []) {
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!isNative || !uid) return;
    // Same 800ms debounce as useAlarmSync/useNotificationSync, so a burst of
    // edits produces one write rather than one per keystroke.
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      let next = null;
      try {
        next = soonestTrigger({ tasks, calEvents, calReminders, taskOccurrenceOverrides, calEventOccurrenceOverrides, habits, habitLogs, habitSkips });
      } catch (e) {
        // Don't write a guess: a wrong value could park this device outside
        // the cron's query. Leaving the old one alone means the server's sweep
        // corrects it, and the local alarm path is untouched either way.
        // eslint-disable-next-line no-console
        console.warn('[Clarity] could not compute nextAlarmAt', e);
        return;
      }
      writeHint(uid, next);
    }, 800);
    return () => clearTimeout(debounceRef.current);
  }, [uid, tasks, calEvents, calReminders, taskOccurrenceOverrides, calEventOccurrenceOverrides, habits, habitLogs, habitSkips]);
}
